// 1-D — show_display columns가 buffer item field에 매칭 안 되는 경우 차단 + valid column 자동 제안.
//
// 문제 패턴 (L5-MEMBERSHIP-TOPATTEND-001):
//   사용자: "2월 3월 4월 출석 상위 회원 비교"
//   merge_slots(matchKey=memberId, mode=inner) → buffer items = [{memberId, name, attendedCount, ...}]
//   LLM show_display columns=['name', '2월_usageId', '3월_usageId', '4월_usageId']
//   → '2월_usageId' 등 합성 컬럼이 buffer item field에 없음 → 빈 셀
//
// 본질 차단:
//   show_display 진입 시 columns 검증 — buffer items[0] keys 또는 LABEL_OVERRIDES alias 또는
//   meta prefix (_change_/_changeRate_/period/_label/_count)에 매칭 안 되는 column이 1개 이상이면
//   1회 retry message 반환 (valid column 후보 명시). 2회+ 시 통과 (false-positive 안전망).

const HEADER = '[SHOW_DISPLAY COLUMN GUARD]';

// buffer item에 존재하지 않아도 valid한 meta prefix/suffix 패턴.
// merge 결과 파생 필드 (_change_/_changeRate_) + period stack (1월_/2월_/3월_ + numeric field) 등.
function isValidMetaColumn(col, dataKeys) {
  if (typeof col !== 'string' || col.length === 0) return false;
  // meta prefix 직접
  if (col === 'period' || col === '_label' || col === '_count') return true;
  if (col === 'periodStart' || col === 'periodEnd') return true;
  // _change_xxx / _changeRate_xxx 파생
  if (col.startsWith('_change_') || col.startsWith('_changeRate_')) return true;
  // 1월_fieldName / 2월_fieldName / Q1_fieldName 등 period prefix
  // base field name이 dataKeys에 있어야 valid (예: "2월_count" → "count" in dataKeys)
  const periodPrefixMatch = col.match(/^(\d{1,2}월|[QqTtFf]\d|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|base|compare_\d+)_(.+)$/i);
  if (periodPrefixMatch && dataKeys.includes(periodPrefixMatch[2])) return true;
  return false;
}

/**
 * column 1개 검증 — buffer items[0] keys, LABEL_OVERRIDES, FIELD_REGISTRY alias, meta prefix 검사.
 *
 * @param {string} col
 * @param {Array<string>} dataKeys      — items[0] Object.keys
 * @param {object} labelOverrides       — LABEL_OVERRIDES 객체 (필드 → 라벨)
 * @param {object} fieldRegistry        — FIELD_REGISTRY 객체 (필드 → {aliases})
 * @returns {boolean}
 */
export function isColumnValid(col, dataKeys, labelOverrides = {}, fieldRegistry = {}) {
  if (typeof col !== 'string' || col.length === 0) return false;
  // 직접 매칭
  if (dataKeys.includes(col)) return true;
  // LABEL_OVERRIDES 역방향 (한국어 라벨 → 필드)
  for (const [field, label] of Object.entries(labelOverrides)) {
    if (label === col && dataKeys.includes(field)) return true;
  }
  // FIELD_REGISTRY alias
  for (const [field, config] of Object.entries(fieldRegistry)) {
    const allNames = [field, ...(config?.aliases || [])];
    if (allNames.includes(col) && allNames.some(f => dataKeys.includes(f))) return true;
  }
  // meta prefix/suffix
  if (isValidMetaColumn(col, dataKeys)) return true;
  return false;
}

/**
 * axisSchemas 중 sibling axis에 invalid column이 정의돼 있는지 확인.
 * multi-axis intention(예: _groupBy=lesson/coach/day/time)에서 LLM이 다른 axis의 field로 show_display
 * 시도 시 axis-switch (call_api with different groupBy) 제안.
 *
 * @param {Array<string>} invalidCols
 * @param {object|null} axisSchemas    — { "_groupBy=lesson": {field:meta, ...}, "_groupBy=time": {...}, ... }
 * @param {string|null} currentAxisKey — 현재 resolved axis key
 * @returns {Array<{column: string, siblingAxisKey: string, siblingAxisLabel: string}>}
 */
function findSiblingAxisMatches(invalidCols, axisSchemas, currentAxisKey) {
  if (!axisSchemas || typeof axisSchemas !== 'object') return [];
  const matches = [];
  for (const col of invalidCols) {
    for (const [axisKey, schema] of Object.entries(axisSchemas)) {
      if (axisKey === currentAxisKey) continue;
      if (schema && typeof schema === 'object' && Object.prototype.hasOwnProperty.call(schema, col)) {
        // schema[col] 라벨 추출 (meta object 형태 — { label, dimension } / string / null)
        const meta = schema[col];
        const label = (meta && typeof meta === 'object' && meta.label) ? meta.label : null;
        matches.push({ column: col, siblingAxisKey: axisKey, siblingAxisLabel: label });
        break;  // 한 column에 대해 첫 매칭 sibling만 사용
      }
    }
  }
  return matches;
}

/**
 * axisKey ("_groupBy=time") → groupBy param value ("time") 추출.
 * generic: prefix("_groupBy="/"_granularity="/"_mode=" 등) 자동 제거.
 *
 * @param {string} axisKey
 * @returns {{paramName: string, paramValue: string}|null}
 */
function parseAxisKey(axisKey) {
  if (typeof axisKey !== 'string') return null;
  const m = axisKey.match(/^_([a-zA-Z][a-zA-Z0-9_]*)=(.+)$/);
  if (!m) return null;
  return { paramName: m[1], paramValue: m[2] };
}

/**
 * 한 axis schema 에서 axis 식별 field의 라벨 토큰 추출 — generic 알고리즘.
 * **primary identifier 기반**: 각 axis schema의 첫 usable field(typically axis 정의 field)의 label만 사용.
 *   예: _groupBy=time의 classTime("수업 시간대"), _groupBy=lesson의 lessonName("수업명"),
 *       _groupBy=day의 dayOfWeek("요일"), _groupBy=coach의 (teacherId skip) coachName("코치명").
 * secondary field(예: lesson axis의 teacherNames "담당 코치명")는 다른 axis 키워드를 포함하여
 * cross-axis confusion 유발 → primary만 사용.
 *
 * @param {object} axisSchemas
 * @param {string} currentAxisKey
 * @returns {Map<string, Set<string>>} sibling axisKey → 식별 토큰 set
 */
function computeAxisIdentifierTokens(axisSchemas, currentAxisKey) {
  const tokenMap = new Map();
  if (!axisSchemas || typeof axisSchemas !== 'object') return tokenMap;

  const isUsableLabel = (field, label) => {
    if (!label || typeof label !== 'string') return false;
    if (/Id$/.test(field) || /ID$/.test(label)) return false;
    if (/profile|image/i.test(field)) return false;
    return true;
  };
  const extractTokens = (label) => {
    const out = new Set();
    const cleaned = label.replace(/\([^)]*\)/g, '').replace(/[%()\d\s\-]+/g, ' ');
    for (const t of cleaned.split(/\s+/)) {
      const trimmed = t.trim();
      if (trimmed.length >= 2 && /[ㄱ-힝]/.test(trimmed)) {
        out.add(trimmed);
        // Korean 2-char prefix 추가 (suffix "명/별/대" 등 dropped form 대응)
        //   예: "코치명" → "코치", "시간대" → "시간" 도 후보 token
        //   3+ char token만 prefix화 (false positive 방지)
        if (trimmed.length >= 3) {
          const prefix = trimmed.slice(0, 2);
          if (/^[ㄱ-힝]{2}$/.test(prefix)) out.add(prefix);
        }
      }
    }
    return out;
  };

  for (const [axisKey, schema] of Object.entries(axisSchemas)) {
    if (axisKey === currentAxisKey) continue;
    if (!schema || typeof schema !== 'object') continue;

    // primary identifier — 첫 usable field 라벨
    const primaryTokens = new Set();
    for (const [field, meta] of Object.entries(schema)) {
      const label = (meta && typeof meta === 'object' && meta.label) ? meta.label : null;
      if (!isUsableLabel(field, label)) continue;
      for (const tok of extractTokens(label)) primaryTokens.add(tok);
      break;  // 첫 usable field만 — secondary field는 cross-axis 혼동 유발
    }
    if (primaryTokens.size > 0) tokenMap.set(axisKey, primaryTokens);
  }
  return tokenMap;
}

/**
 * 사용자 입력이 sibling axis의 식별 토큰을 포함하는지 검사 — axis-switch 의도 감지.
 * generic: schema label 기반 substring 매칭. 현재 axis의 토큰과 겹치는 경우는 ambiguous로 처리(skip).
 *
 * @param {string} userInput
 * @param {object} axisSchemas
 * @param {string|null} currentAxisKey
 * @returns {Array<{siblingAxisKey: string, matchedToken: string}>}
 */
export function detectAxisIntentMismatch(userInput, axisSchemas, currentAxisKey) {
  if (typeof userInput !== 'string' || userInput.length === 0) return [];
  if (!axisSchemas || !currentAxisKey) return [];
  const siblingTokens = computeAxisIdentifierTokens(axisSchemas, currentAxisKey);
  if (siblingTokens.size === 0) return [];
  // 현재 axis의 primary identifier 토큰 (첫 usable field 기준)
  // computeAxisIdentifierTokens는 currentAxisKey와 같은 entry를 skip하므로 별도 호출
  const currentTokens = computeAxisIdentifierTokens(
    { [currentAxisKey]: axisSchemas[currentAxisKey] },
    '__noSkip__'  // dummy key — current axis가 skip되지 않도록
  ).get(currentAxisKey) || new Set();

  // 모든 axis(current + sibling)의 token first-occurrence position in userInput을 비교
  //   "코치 수업" → "코치"(pos 0) vs "수업"(pos 3) → "코치" 먼저 = coach 우선
  //   "수업 코치" → "수업"(pos 0) vs "코치"(pos 3) → "수업" 먼저 = lesson 우선
  //   "어떤 시간대가" → "시간대"(pos 3) → time
  //   first-occurrence-wins: user가 먼저 언급한 axis가 primary 의도
  const allAxisTokens = new Map();  // axisKey → tokens
  allAxisTokens.set(currentAxisKey, currentTokens);
  for (const [k, v] of siblingTokens) allAxisTokens.set(k, v);

  let bestAxis = null;
  let bestPos = Infinity;
  let bestToken = null;
  for (const [axisKey, tokens] of allAxisTokens) {
    for (const tok of tokens) {
      const pos = userInput.indexOf(tok);
      if (pos >= 0 && pos < bestPos) {
        bestPos = pos;
        bestAxis = axisKey;
        bestToken = tok;
      }
    }
  }

  // primary 의도가 current axis와 일치 → switch 불필요
  if (bestAxis === null || bestAxis === currentAxisKey) return [];

  // [COMPOUND QUERY EXEMPTION] user input contains tokens from BOTH current axis AND best sibling axis →
  //   not an axis-switch intent. user wants both dimensions; current axis often has the sibling's identifier as a column
  //   (e.g. groupBy=lesson rows contain teacherNames column). Forcing axis switch would lose the compound info.
  //   generic: any multi-axis schema where current axis tokens AND sibling axis tokens both appear in input.
  const currentAxisHasTokenInInput = Array.from(currentTokens).some(tok => userInput.indexOf(tok) >= 0);
  if (currentAxisHasTokenInInput) return [];

  return [{ siblingAxisKey: bestAxis, matchedToken: bestToken }];
}

/**
 * 한 axis schema에서 primary identifier field name 추출 — generic 알고리즘.
 * computeAxisIdentifierTokens와 동일한 "첫 usable field" 규칙으로 field NAME 반환.
 *   예: _groupBy=coach → "coachName" (teacherId/coachProfile skip)
 *       _groupBy=lesson → "lessonName"
 *       _groupBy=day → "dayOfWeek"
 *       _groupBy=time → "classTime"
 *
 * @param {object} schema  — axis schema (e.g. axisSchemas[currentAxisKey])
 * @returns {string|null}  — primary identifier field name 또는 null
 */
function getPrimaryIdentifierField(schema) {
  if (!schema || typeof schema !== 'object') return null;
  const isUsableLabel = (field, label) => {
    if (!label || typeof label !== 'string') return false;
    if (/Id$/.test(field) || /ID$/.test(label)) return false;
    if (/profile|image/i.test(field)) return false;
    return true;
  };
  for (const [field, meta] of Object.entries(schema)) {
    const label = (meta && typeof meta === 'object' && meta.label) ? meta.label : null;
    if (!isUsableLabel(field, label)) continue;
    return field;  // 첫 usable field — axis primary identifier
  }
  return null;
}

/**
 * field name이 dataKeys 또는 alias 경로로 존재하는지 확인 + 실제 dataKey 반환.
 *   예: getPrimaryFieldInData("coachName", ["name", "totalSessions", ...], fieldRegistry)
 *       → "name" (coachName alias → name이 dataKeys에 존재)
 *
 * @param {string} field
 * @param {Array<string>} dataKeys
 * @param {object} fieldRegistry
 * @returns {string|null}
 */
function resolveFieldInDataKeys(field, dataKeys, fieldRegistry) {
  if (typeof field !== 'string' || !Array.isArray(dataKeys)) return null;
  if (dataKeys.includes(field)) return field;
  if (fieldRegistry && typeof fieldRegistry === 'object') {
    // direct entry: field === canonical
    const directConfig = fieldRegistry[field];
    if (directConfig?.aliases) {
      const found = directConfig.aliases.find(a => dataKeys.includes(a));
      if (found) return found;
    }
    // reverse: field is in some canonical's aliases
    for (const [canonical, config] of Object.entries(fieldRegistry)) {
      if (config?.aliases?.includes(field) && dataKeys.includes(canonical)) {
        return canonical;
      }
    }
  }
  return null;
}

/**
 * columns 배열 검증 + valid/invalid 분리.
 *
 * @param {object} ctx
 * @param {Array<string>} ctx.columns
 * @param {Array<object>} ctx.bufferItems
 * @param {object} [ctx.labelOverrides]
 * @param {object} [ctx.fieldRegistry]
 * @param {boolean} [ctx.retryAttempted]
 * @param {object|null} [ctx.axisSchemas]    — multi-axis intention 형제 axis 스키마
 * @param {string|null} [ctx.currentAxisKey] — 현재 resolved axis key
 * @param {string|null} [ctx.intention]      — buffer intention (axis-switch suggestion 메시지용)
 * @returns {{
 *   block: boolean,
 *   invalidColumns?: string[],
 *   validColumns?: string[],
 *   dataKeys?: string[],
 *   suggested?: string[],
 *   message?: string,
 *   siblingAxisMatches?: Array<{column: string, siblingAxisKey: string}>,
 * }}
 */
export function evaluateShowDisplayColumns(ctx = {}) {
  const {
    columns, bufferItems, labelOverrides = {}, fieldRegistry = {}, retryAttempted,
    axisSchemas = null, currentAxisKey = null, intention = null,
  } = ctx;
  if (retryAttempted) return { block: false };
  if (!Array.isArray(columns) || columns.length === 0) return { block: false };
  if (!Array.isArray(bufferItems) || bufferItems.length === 0 || !bufferItems[0]) return { block: false };

  const dataKeys = Object.keys(bufferItems[0]);
  if (dataKeys.length === 0) return { block: false };

  const invalid = [];
  const valid = [];
  for (const col of columns) {
    if (isColumnValid(col, dataKeys, labelOverrides, fieldRegistry)) {
      valid.push(col);
    } else {
      invalid.push(col);
    }
  }

  // [AXIS_IDENTIFIER_GUARD] multi-axis intention 진입 시 axis primary identifier 컬럼 누락 차단.
  //   본질: LLM이 multi-axis 데이터(예: _groupBy=coach/lesson/day/time)를 show_display 시
  //     metric 컬럼(totalSessions/attendedCount 등)만 명시하고 axis 식별 컬럼(coachName/lessonName/dayOfWeek/classTime)을
  //     누락하면 사용자가 "어느 코치/수업/요일인지" 알 수 없는 표가 노출됨.
  //   조건:
  //     (1) axisSchemas + currentAxisKey 존재 (multi-axis intention 로드)
  //     (2) currentAxis schema의 primary identifier field (첫 usable field) 추출 가능
  //     (3) 해당 identifier가 dataKeys 또는 alias 경로로 존재 (실제 데이터에 매핑 가능)
  //     (4) LLM columns에 identifier (또는 alias) 미포함
  //   → block + retry message (identifier 추가 안내) + suggested에 identifier 우선 배치.
  //   ID_ONLY_GUARD 보다 먼저 평가 — multi-axis 누락이 raw PK 누락보다 본질적 user-facing 이슈.
  if (axisSchemas && currentAxisKey && axisSchemas[currentAxisKey]) {
    const _axisSchema = axisSchemas[currentAxisKey];
    const _primaryField = getPrimaryIdentifierField(_axisSchema);
    if (_primaryField) {
      const _resolvedInData = resolveFieldInDataKeys(_primaryField, dataKeys, fieldRegistry);
      if (_resolvedInData) {
        // LLM columns에 identifier (canonical 또는 alias) 포함 여부 — alias 풀어서 매칭
        const _identifierAliases = new Set([_primaryField, _resolvedInData]);
        if (fieldRegistry?.[_primaryField]?.aliases) {
          for (const a of fieldRegistry[_primaryField].aliases) _identifierAliases.add(a);
        }
        if (fieldRegistry?.[_resolvedInData]?.aliases) {
          for (const a of fieldRegistry[_resolvedInData].aliases) _identifierAliases.add(a);
        }
        const _hasIdentifier = columns.some(c => _identifierAliases.has(c));
        if (!_hasIdentifier) {
          const _identifierLabel = (_axisSchema[_primaryField]?.label) || _primaryField;
          const _suggested = [_resolvedInData, ...columns];
          return {
            block: true,
            invalidColumns: [],
            validColumns: columns,
            dataKeys,
            suggested: _suggested,
            message: (
              `${HEADER} columns에 현재 axis(${currentAxisKey})의 primary identifier "${_resolvedInData}"(라벨: "${_identifierLabel}")가 누락됐습니다. ` +
              `multi-axis 데이터(metric만 표시) 시 어느 entity의 metric인지 알 수 없습니다. ` +
              `columns 맨 앞에 "${_resolvedInData}" 추가 후 show_display 재호출하세요. ` +
              `현재 columns: [${columns.map(c => `"${c}"`).join(', ')}] → 권장: [${_suggested.map(c => `"${c}"`).join(', ')}].`
            ),
          };
        }
      }
    }
  }

  // [1-J Layer B] ID_ONLY_GUARD — raw PK column만 있고 자연어 column 누락 시 block + suggest replacement.
  //   본질: LLM이 columns argument 직접 명시 시 memberId/userId 같은 raw PK 노출 차단.
  //   data에 name field 보유한 경우만 발동 (false-positive 안전망 — data에 name 자체 없으면 raw PK 강행).
  const ID_FIELDS = new Set(['memberId', 'userId', 'teacherId', 'memberShipId', 'productId']);
  const NAME_FIELDS_LIST = ['name', 'memberName', 'memberShipName', 'productName', 'itemName', 'lessonName', 'className', 'title'];
  const hasIdField = columns.some(c => ID_FIELDS.has(c));
  const hasNameField = columns.some(c => NAME_FIELDS_LIST.includes(c) || /name|title|label/i.test(c));
  const dataKeyNames = dataKeys.filter(k => NAME_FIELDS_LIST.includes(k));
  if (hasIdField && !hasNameField && dataKeyNames.length > 0) {
    const idCols = columns.filter(c => ID_FIELDS.has(c));
    return {
      block: true,
      invalidColumns: idCols,
      validColumns: columns.filter(c => !ID_FIELDS.has(c)),
      dataKeys,
      suggested: [...dataKeyNames, ...columns.filter(c => !ID_FIELDS.has(c))],
      message: (
        `${HEADER} columns에 raw 식별자(${idCols.map(c => `"${c}"`).join(', ')})만 있고 자연어 column(name/title 등)이 누락됐습니다. ` +
        `data에 [${dataKeyNames.map(c => `"${c}"`).join(', ')}] 사용 가능. ` +
        `columns에서 ID field 제거 + name field 추가 후 show_display 재호출.`
      ),
    };
  }

  if (invalid.length === 0) return { block: false };

  // 모든 column이 invalid이면 강행 (LLM이 다른 결정 불가능한 상황)
  if (valid.length === 0) return { block: false };

  // [AXIS SWITCH SUGGESTION] invalid column이 sibling axis 스키마에 정의돼 있는가?
  //   YES → LLM이 wrong axis buffer로 시도 중. axis-switch (call_api with different param) 제안.
  //   generic: groupBy/granularity/mode 등 axis prefix 자동 감지.
  const siblingMatches = findSiblingAxisMatches(invalid, axisSchemas, currentAxisKey);

  // suggested = valid columns + dataKeys 외 일부 추천 (entity-level identifier 우선)
  const suggested = [...valid];
  const ENTITY_HINT_KEYS = ['name', 'memberName', 'memberShipName', 'productName', 'itemName', 'lessonName', 'title', 'period', '_count', '_label'];
  for (const k of ENTITY_HINT_KEYS) {
    if (dataKeys.includes(k) && !suggested.includes(k)) suggested.push(k);
  }

  let message;
  if (siblingMatches.length > 0) {
    // axis-switch suggestion이 우선 (LLM이 column 단순 교체로는 본질 해결 불가)
    const currentParsed = currentAxisKey ? parseAxisKey(currentAxisKey) : null;
    const switchHints = [];
    const seenAxis = new Set();
    for (const m of siblingMatches) {
      if (seenAxis.has(m.siblingAxisKey)) continue;
      seenAxis.add(m.siblingAxisKey);
      const parsed = parseAxisKey(m.siblingAxisKey);
      if (parsed) {
        const labelTxt = m.siblingAxisLabel ? ` (라벨: "${m.siblingAxisLabel}")` : '';
        switchHints.push(`  - "${m.column}"${labelTxt} → ${parsed.paramName}="${parsed.paramValue}" axis`);
      }
    }
    const intentionTxt = intention ? `"${intention}"` : '현재 intention';
    const currentAxisTxt = currentParsed
      ? `현재 buffer는 ${currentParsed.paramName}="${currentParsed.paramValue}" axis로 로드돼 있습니다.`
      : '';
    // 첫 sibling axis를 권장 call_api 예시로 사용
    const recParsed = parseAxisKey(siblingMatches[0].siblingAxisKey);
    const recExample = recParsed
      ? `call_api({ intention: ${intentionTxt}, params: { ${recParsed.paramName}: "${recParsed.paramValue}" } })`
      : `call_api(${intentionTxt}, <axis param>)`;
    message = (
      `${HEADER} columns [${invalid.map(c => `"${c}"`).join(', ')}]이(가) 현재 buffer axis와 일치하지 않습니다. ` +
      `이 column들은 ${intentionTxt}의 다른 axis 스키마에 정의돼 있습니다:\n` +
      `${switchHints.join('\n')}\n` +
      `${currentAxisTxt}\n` +
      `axis를 바꾸려면 column 교체가 아니라 call_api를 새 axis param으로 재호출해야 합니다:\n` +
      `  ${recExample}\n` +
      `사용자가 원하는 axis로 call_api 재호출 후 show_display 진행하세요. ` +
      `axis 변경이 아니면 valid 후보로 교체: [${suggested.slice(0, 8).map(c => `"${c}"`).join(', ')}]`
    );
  } else {
    message = (
      `${HEADER} columns [${invalid.map(c => `"${c}"`).join(', ')}]이(가) buffer item field에 존재하지 않습니다 ` +
      `(buffer 첫 row keys: [${dataKeys.slice(0, 15).join(', ')}${dataKeys.length > 15 ? ', ...' : ''}]).\n` +
      `valid 후보: [${suggested.slice(0, 10).map(c => `"${c}"`).join(', ')}]\n` +
      `columns에서 invalid column 제거 또는 valid 후보로 교체 후 show_display 재호출하세요.`
    );
  }

  return {
    block: true,
    invalidColumns: invalid,
    validColumns: valid,
    dataKeys,
    suggested,
    message,
    siblingAxisMatches: siblingMatches.length > 0 ? siblingMatches : undefined,
  };
}

export const _HEADER = HEADER;
